import { useEffect, useRef, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';

import './styles/ProfileBadgeClaimExperience.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const CLOSED_CHEST_URL = `${PUBLIC_URL}/suarte-road-assets/ClosedChest.svg`;
const OPEN_CHEST_URL = `${PUBLIC_URL}/suarte-road-assets/OpenChest.svg`;
const VIDEO_URL = 'https://video.files.suarte.art/background-mobile.mp4';
const MAX_TAPS = 12;

export default function ProfileBadgeClaimExperience({ badge, onClose, onClaimed }) {
  const t = useTranslations('common.profile.badges.claim_experience');
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(0);
  const [lastTapTime, setLastTapTime] = useState(Date.now());
  const [textAnimating, setTextAnimating] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showBadgeReward, setShowBadgeReward] = useState(false);
  const [showReceivedText, setShowReceivedText] = useState(false);
  const [showBadgeTitle, setShowBadgeTitle] = useState(false);
  const [showRewardDetails, setShowRewardDetails] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const timersRef = useRef([]);

  const registerTimeout = (callback, delay) => {
    const timeoutId = setTimeout(() => {
      callback();
      timersRef.current = timersRef.current.filter((id) => id !== timeoutId);
    }, delay);
    timersRef.current.push(timeoutId);
  };

  const clearAllTimeouts = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, []);

  useEffect(() => {
    clearAllTimeouts();
    setProgress(0);
    setStage(0);
    setLastTapTime(Date.now());
    setTextAnimating(false);
    setIsAnimating(false);
    setShowBadgeReward(false);
    setShowReceivedText(false);
    setShowBadgeTitle(false);
    setShowRewardDetails(false);
    setIsClaiming(false);
  }, [badge?.id]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (stage === 0 && progress < MAX_TAPS && Date.now() - lastTapTime > 1000 && progress > 0) {
        setProgress((prev) => Math.max(prev - 0.1, 0));
      }
    }, 50);

    return () => clearInterval(interval);
  }, [lastTapTime, progress, stage]);

  useEffect(() => {
    if (progress >= MAX_TAPS && stage === 0) {
      setStage(1);
      registerTimeout(() => {
        registerTimeout(() => {
          setStage(2);
        }, 300);
      }, 1000);
    }
  }, [progress, stage]);

  const handleChestClick = () => {
    if (progress >= MAX_TAPS || stage !== 0) return;
    setProgress((prev) => Math.min(prev + 1, MAX_TAPS));
    setIsAnimating(false);
    setTextAnimating(false);
    registerTimeout(() => setIsAnimating(true), 0);
    registerTimeout(() => setTextAnimating(true), 0);
    setLastTapTime(Date.now());
  };

  const handleChestFadeOutEnd = () => {
    registerTimeout(() => {
      setShowBadgeReward(true);
    }, 120);

    registerTimeout(() => {
      setShowReceivedText(true);
      setShowBadgeTitle(true);
      setShowRewardDetails(true);
    }, 900);
  };

  const handleClose = async () => {
    if (isClaiming) {
      return;
    }

    try {
      if (badge?.id) {
        setIsClaiming(true);
        await onClaimed?.(badge);
      }
    } catch (error) {
      console.error('Failed to confirm badge claim', error);
    } finally {
      setIsClaiming(false);
      onClose?.();
    }
  };

  if (!badge) return null;
  const badgeImage = badge.image || badge.fallbackImage || null;
  const badgeTitle = badge.rewardTitle || badge.name || t('fallback.badge_unlocked');

  return (
    <div className="badge-claim__overlay">
      <div className="badge-claim__page-container">
        <video className="badge-claim__background-video" autoPlay loop muted playsInline>
          <source src={VIDEO_URL} type="video/mp4" />
          {t('messages.video_not_supported')}
        </video>

        {progress < MAX_TAPS && (
          <h2 className={`badge-claim__tap-text ${textAnimating ? 'is-animating' : ''} ${stage >= 1 ? 'is-hidden' : ''}`}>
            <span className="badge-claim__tap-text-border">{t('messages.tap_the_chest')}</span>
          </h2>
        )}

        {stage !== 2 && (
          <img
            src={CLOSED_CHEST_URL}
            alt={t('aria.closed_chest')}
            className={`badge-claim__closed-chest ${stage === 1 ? 'is-shaking' : isAnimating ? 'is-tapping' : ''}`}
            onClick={handleChestClick}
          />
        )}

        {stage === 2 && (
          <img
            src={OPEN_CHEST_URL}
            alt={t('aria.open_chest')}
            className="badge-claim__open-chest"
            onAnimationEnd={handleChestFadeOutEnd}
          />
        )}

        {showBadgeReward && (
          <div className="badge-claim__reward-content">
            <div className={`badge-claim__reward-details ${showRewardDetails ? 'is-visible' : ''}`}>
              {showReceivedText && (
                <p className="badge-claim__received-text">{t('messages.you_received')}</p>
              )}
              {showBadgeTitle && (
                <p className="badge-claim__badge-title animated-shine">{badgeTitle}</p>
              )}
            </div>

            <div className="badge-claim__badge-stack">
              {badgeImage && (
                <img
                  src={badgeImage}
                  alt={badge.name}
                  className="badge-claim__badge-image"
                />
              )}
            </div>

            <button
              type="button"
              className={`badge-claim__close-button ${showRewardDetails ? 'is-visible' : ''}`}
              onClick={handleClose}
              disabled={isClaiming}
            >
              {isClaiming ? t('actions.claiming') : t('actions.exit')}
            </button>
          </div>
        )}

        <div className={`badge-claim__progress ${stage >= 2 ? 'is-fading' : ''}`}>
          <div
            className="badge-claim__progress-fill"
            style={{ width: `${(progress / MAX_TAPS) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}
